import { TABLE_FUNCTION_NAME, VARIABLE_PARTS_SEPARATOR, VARIABLE_PREFIXES } from "./formulaEditor.constants";
// @ts-ignore
import FormulaLexer from "@/lib/formula/FormulaLexer";
import { getTokens } from "./formulaEditor.antlr";
import { AntlrCompletionContext, FunctionCallContext, Variable, VariableType } from "./formulaEditor.types";

function extractVariableFromTokens(rawTextBeforeCaret: string, tokens: any[]): Variable {
	if (!tokens.length) {
		return { code: "" };
	}

	let startIndex = rawTextBeforeCaret.length;
	let sawIdentifier = false;

	for (let index = tokens.length - 1; index >= 0; index--) {
		const token = tokens[index];

		if (token.type === FormulaLexer.IDENTIFIER || token.type === FormulaLexer.T__0) {
			startIndex = token.start;
			sawIdentifier ||= token.type === FormulaLexer.IDENTIFIER;
			continue;
		}

		break;
	}

	if (!sawIdentifier) {
		return { code: "" };
	}

	const rawVariable = rawTextBeforeCaret.substring(startIndex).trim();
	if (!rawVariable) {
		return { code: "" };
	}

	const firstSeparatorIndex = rawVariable.indexOf(VARIABLE_PARTS_SEPARATOR);
	if (firstSeparatorIndex < 0) {
		return { code: rawVariable };
	}

	const extractedPrefix = rawVariable.substring(0, firstSeparatorIndex);
	const code = rawVariable.substring(firstSeparatorIndex + 1);
	const variable: Variable = { code };

	if (VARIABLE_PREFIXES.some(prefix => prefix === extractedPrefix)) {
		variable.prefix = extractedPrefix as VariableType;
	}

	return variable;
}

export function extractVariable(rawTextBeforeCaret: string): Variable {
	return extractVariableFromTokens(rawTextBeforeCaret, getTokens(rawTextBeforeCaret));
}

function extractFunctionCallContextFromTokens(textBeforeCaret: string, tokens: any[]): FunctionCallContext | null {
	if (!tokens.length) {
		return null;
	}

	let depth = 0;
	let openParenIndex = -1;
	let functionName = "";

	for (let index = tokens.length - 1; index >= 0; index--) {
		const token = tokens[index];

		if (token.text === ")") {
			depth++;
			continue;
		}

		if (token.text !== "(") {
			continue;
		}

		depth--;
		if (depth >= 0) {
			continue;
		}

		if (index > 0 && tokens[index - 1].type === FormulaLexer.IDENTIFIER) {
			openParenIndex = token.start;
			functionName = (tokens[index - 1].text || "").trim();
			break;
		}

		return null;
	}

	if (openParenIndex < 0) {
		return null;
	}

	const args: string[] = [];
	let current = "";
	let currentArgStartOffset = openParenIndex + 1;
	let inString = false;
	let forwardDepth = 0;

	for (let index = openParenIndex + 1; index < textBeforeCaret.length; index++) {
		const character = textBeforeCaret[index];

		if (character === '"') {
			const nextCharacter = textBeforeCaret[index + 1];
			if (inString && nextCharacter === '"') {
				current += character + nextCharacter;
				index++;
				continue;
			}

			inString = !inString;
			current += character;
			continue;
		}

		if (inString) {
			current += character;
			continue;
		}

		if (character === "(") {
			forwardDepth++;
		} else if (character === ")") {
			forwardDepth--;
		}

		if (forwardDepth === 0 && character === ",") {
			args.push(current.trim());
			current = "";
			currentArgStartOffset = index + 1;
			continue;
		}

		current += character;
	}

	args.push(current.trim());

	return {
		args,
		currentArgIndex: args.length - 1,
		currentArgStartOffset,
		functionName
	};
}

export function resolveCompletionContext(textBeforeCaret: string): AntlrCompletionContext {
	const tokens = getTokens(textBeforeCaret);
	const functionContext = extractFunctionCallContextFromTokens(textBeforeCaret, tokens);

	if (functionContext?.functionName.toLowerCase() === TABLE_FUNCTION_NAME) {
		return {
			kind: "table",
			tableContext: {
				args: functionContext.args,
				currentArgIndex: functionContext.currentArgIndex,
				currentArgStartOffset: functionContext.currentArgStartOffset
			},
			currentArgRaw: functionContext.args[functionContext.currentArgIndex] || "",
			rangeStartOffset: functionContext.currentArgStartOffset,
			rangeEndOffset: textBeforeCaret.length
		};
	}

	if (functionContext) {
		return {
			kind: "function",
			functionContext,
			currentArgRaw: functionContext.args[functionContext.currentArgIndex] || "",
			rangeStartOffset: functionContext.currentArgStartOffset,
			rangeEndOffset: textBeforeCaret.length
		};
	}

	return {
		kind: "variable",
		variable: extractVariableFromTokens(textBeforeCaret, tokens)
	};
}
